if (!customElements.get('plp-catalogue')) {
  customElements.define(
    'plp-catalogue',
    class PlpCatalogue extends HTMLElement {
      constructor() {
        super();

        this.rail = this.querySelector('[data-plp-rail]');
        this.grid = this.querySelector('[data-plp-grid]');
        this.cards = Array.from(this.querySelectorAll('[data-plp-card]'));
        this.categoryButtons = Array.from(this.querySelectorAll('[data-plp-category]'));
        this.industryInputs = Array.from(this.querySelectorAll('[data-plp-industry]'));
        this.searchInput = this.querySelector('[data-plp-search]');
        this.sortSelect = this.querySelector('[data-plp-sort]');
        this.countEl = this.querySelector('[data-plp-count]');
        this.activeEl = this.querySelector('[data-plp-active]');
        this.emptyEl = this.querySelector('[data-plp-empty]');
        this.titleEl = this.querySelector('[data-plp-title]');

        this.defaultTitle = this.titleEl ? this.titleEl.textContent.trim() : '';
        this.defaultSort = this.dataset.defaultSort || 'featured';

        // Remembers each card's authored order so 'featured' can be restored.
        this.cards.forEach((card, index) => {
          card.dataset.initialOrder = String(index);
        });

        this.state = {
          category: 'all',
          industries: [],
          query: '',
          sort: this.sortSelect ? this.sortSelect.value : this.defaultSort,
        };

        this.hideEmptyIndustries();
        this.readStateFromURL();
        // The URL can name a sort, query or industries, so push that state onto the
        // controls before the first paint. Without this a shared link filters
        // correctly while the select still reads 'Featured', and the next change
        // event reverts the sort to whatever the stale control said.
        this.syncControls();
        this.bindEvents();
        this.render();
      }

      bindEvents() {
        this.categoryButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.state.category = button.dataset.plpCategory;
            this.closeRail();
            this.render();
          });
        });

        this.industryInputs.forEach((input) => {
          input.addEventListener('change', () => {
            this.state.industries = this.industryInputs.filter((i) => i.checked).map((i) => i.value);
            this.render();
          });
        });

        if (this.searchInput) {
          this.searchInput.addEventListener('input', (event) => {
            this.state.query = event.target.value.toLowerCase().trim();
            this.render();
          });
        }

        if (this.sortSelect) {
          this.sortSelect.addEventListener('change', (event) => {
            this.state.sort = event.target.value;
            this.render();
          });
        }

        this.querySelectorAll('[data-plp-clear]').forEach((button) => {
          button.addEventListener('click', () => this.clearFilters());
        });

        const openButton = this.querySelector('[data-plp-rail-open]');
        if (openButton) openButton.addEventListener('click', () => this.openRail());

        const closeButton = this.querySelector('[data-plp-rail-close]');
        if (closeButton) closeButton.addEventListener('click', () => this.closeRail());

        window.addEventListener('popstate', () => {
          this.readStateFromURL();
          this.syncControls();
          this.render({ updateURL: false });
        });
      }

      openRail() {
        if (this.rail) this.rail.classList.add('is-open');
      }

      closeRail() {
        if (this.rail) this.rail.classList.remove('is-open');
      }

      /**
       * Industry values come from a metafield that falls back to product tags, so a
       * store without that data would render a column of options that match nothing.
       * Drop any option no card carries, and the whole facet if none survive.
       */
      hideEmptyIndustries() {
        if (!this.industryInputs.length) return;

        const present = new Set();
        this.cards.forEach((card) => {
          (card.dataset.industries || '')
            .split(' ')
            .filter(Boolean)
            .forEach((key) => present.add(key));
        });

        let remaining = 0;
        this.industryInputs.forEach((input) => {
          const item = input.closest('li');
          if (present.has(input.value)) {
            remaining += 1;
          } else if (item) {
            item.hidden = true;
          }
        });

        this.industryInputs = this.industryInputs.filter((input) => present.has(input.value));

        if (remaining === 0) {
          const facet = this.querySelector('.plp__facet');
          if (facet) facet.hidden = true;
        }
      }

      readStateFromURL() {
        const params = new URLSearchParams(window.location.search);

        // A collection URL opens on that category; /collections/all opens on everything.
        const handle = this.dataset.currentHandle;
        let initialCategory = 'all';
        if (handle && handle !== 'all') {
          const match = this.categoryButtons.find((b) => b.dataset.plpCategory === handle);
          if (match) initialCategory = handle;
        }

        this.state.category = params.get('category') || initialCategory;
        this.state.query = (params.get('q') || '').toLowerCase().trim();
        this.state.sort = params.get('sort') || this.defaultSort;

        const industries = params.get('industry');
        this.state.industries = industries ? industries.split(',').filter(Boolean) : [];
      }

      syncControls() {
        if (this.searchInput) this.searchInput.value = this.state.query;
        if (this.sortSelect) this.sortSelect.value = this.state.sort;

        this.industryInputs.forEach((input) => {
          input.checked = this.state.industries.includes(input.value);
        });
      }

      updateURL() {
        const params = new URLSearchParams();

        if (this.state.category !== 'all') params.set('category', this.state.category);
        if (this.state.industries.length) params.set('industry', this.state.industries.join(','));
        if (this.state.query) params.set('q', this.state.query);
        if (this.state.sort !== this.defaultSort) params.set('sort', this.state.sort);

        const query = params.toString();
        history.replaceState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
      }

      /**
       * Keys of the active category plus, when a parent is selected, its nested children.
       * A tile matches if it carries any of them.
       */
      get activeCategoryKeys() {
        if (this.state.category === 'all') return null;

        const keys = [this.state.category];
        this.categoryButtons.forEach((button) => {
          if (button.dataset.plpParent === this.state.category) {
            keys.push(button.dataset.plpCategory);
          }
        });

        return keys;
      }

      matches(card) {
        const categoryKeys = this.activeCategoryKeys;

        if (categoryKeys) {
          const cardCategories = card.dataset.categories || '';
          const inCategory = categoryKeys.some((key) => cardCategories.includes(` ${key} `));
          if (!inCategory) return false;
        }

        if (this.state.industries.length) {
          const cardIndustries = card.dataset.industries || '';
          const inIndustry = this.state.industries.some((key) => cardIndustries.includes(` ${key} `));
          if (!inIndustry) return false;
        }

        if (this.state.query) {
          const name = (card.dataset.name || '').toLowerCase();
          if (!name.includes(this.state.query)) return false;
        }

        return true;
      }

      sortCards(visible) {
        const number = (card, key) => Number(card.dataset[key] || 0);

        const comparators = {
          featured: (a, b) => number(a, 'initialOrder') - number(b, 'initialOrder'),
          'price-asc': (a, b) => number(a, 'price') - number(b, 'price'),
          'price-desc': (a, b) => number(b, 'price') - number(a, 'price'),
          moq: (a, b) => number(a, 'moq') - number(b, 'moq'),
          speed: (a, b) => number(a, 'leadWeeks') - number(b, 'leadWeeks'),
          newest: (a, b) => number(b, 'published') - number(a, 'published'),
        };

        const comparator = comparators[this.state.sort] || comparators.featured;

        // Cards missing the sorted-on value fall to the end rather than to the top.
        const sortKeys = { 'price-asc': 'price', moq: 'moq', speed: 'leadWeeks' };
        const sortKey = sortKeys[this.state.sort];

        const ordered = visible.slice().sort((a, b) => {
          if (sortKey) {
            const aMissing = number(a, sortKey) <= 0;
            const bMissing = number(b, sortKey) <= 0;
            if (aMissing !== bMissing) return aMissing ? 1 : -1;
          }

          return comparator(a, b);
        });

        ordered.forEach((card, index) => {
          card.style.order = String(index);
        });
      }

      renderCategoryState() {
        const activeButton = this.categoryButtons.find((b) => b.dataset.plpCategory === this.state.category);
        const activeParent = activeButton ? activeButton.dataset.plpParent : undefined;

        // A nested category keeps its siblings expanded so the branch stays navigable.
        const expandedParent = activeParent || this.state.category;

        this.categoryButtons.forEach((button) => {
          const isActive = button.dataset.plpCategory === this.state.category;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

          if (button.dataset.plpParent) {
            button.classList.toggle('is-visible', button.dataset.plpParent === expandedParent);
          }
        });

        if (this.titleEl) {
          this.titleEl.textContent = activeButton ? activeButton.textContent.trim() : this.defaultTitle;
        }
      }

      renderActiveChips() {
        if (!this.activeEl) return;

        this.activeEl.innerHTML = '';

        const chips = [];

        if (this.state.category !== 'all') {
          const button = this.categoryButtons.find((b) => b.dataset.plpCategory === this.state.category);
          if (button) {
            chips.push({ label: button.textContent.trim(), clear: () => (this.state.category = 'all') });
          }
        }

        this.state.industries.forEach((key) => {
          const input = this.industryInputs.find((i) => i.value === key);
          const label = input ? input.parentElement.textContent.trim() : key;

          chips.push({
            label,
            clear: () => {
              this.state.industries = this.state.industries.filter((i) => i !== key);
              if (input) input.checked = false;
            },
          });
        });

        if (this.state.query) {
          chips.push({
            label: `"${this.state.query}"`,
            clear: () => {
              this.state.query = '';
              if (this.searchInput) this.searchInput.value = '';
            },
          });
        }

        chips.forEach((chip) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'plp__active-chip';
          button.textContent = chip.label;
          button.addEventListener('click', () => {
            chip.clear();
            this.render();
          });
          this.activeEl.appendChild(button);
        });

        this.activeEl.hidden = chips.length === 0;

        const clearButton = this.rail ? this.rail.querySelector('[data-plp-clear]') : null;
        if (clearButton) clearButton.hidden = chips.length === 0;
      }

      /**
       * The read-more copy below the grid is written for specific categories, so it stays
       * hidden elsewhere. Mirrors the behaviour the previous collection list section had.
       */
      renderReadMore() {
        const readMore = document.querySelector('.shopify-section.read-more');
        if (!readMore) return;

        const keep = ['all', 'custom-boxes', 'custom-cosmetic-packaging'];
        readMore.classList.toggle('hide', !keep.includes(this.state.category));
      }

      clearFilters() {
        this.state.category = 'all';
        this.state.industries = [];
        this.state.query = '';

        this.industryInputs.forEach((input) => {
          input.checked = false;
        });
        if (this.searchInput) this.searchInput.value = '';

        this.render();
      }

      render({ updateURL = true } = {}) {
        const visible = [];

        this.cards.forEach((card) => {
          const isMatch = this.matches(card);
          card.hidden = !isMatch;
          if (isMatch) visible.push(card);
        });

        this.sortCards(visible);
        this.renderCategoryState();
        this.renderActiveChips();
        this.renderReadMore();

        if (this.countEl) {
          this.countEl.textContent = `${visible.length} ${visible.length === 1 ? 'product' : 'products'}`;
        }

        if (this.emptyEl) this.emptyEl.hidden = visible.length > 0;
        if (this.grid) this.grid.hidden = visible.length === 0;

        if (updateURL) this.updateURL();
      }
    },
  );
}
